import { useState } from "react";

interface StatCardProps {
  readonly value: string | number;
  readonly label: string;
  readonly color: string;
  readonly trend?: "up" | "down" | "neutral";
  readonly trendValue?: string;
  readonly progress?: number; // 0-100 pour le cercle de progression
}

const COLOR_STYLES: Record<string, string> = {
  accent: "text-accent",
  success: "text-success",
  "amber-500": "text-amber-500",
  danger: "text-danger",
};

const BG_COLOR_STYLES: Record<string, string> = {
  accent: "bg-accent/10",
  success: "bg-success/10",
  "amber-500": "bg-amber-500/10",
  danger: "bg-danger/10",
};

export function StatCard({ value, label, color, trend, trendValue, progress }: StatCardProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  // Animation d'entrée
  useState(() => {
    setTimeout(() => setIsLoaded(true), 100);
  });

  const trendIcon = trend === "up" ? "↑" : trend === "down" ? "↓" : "–";
  const trendColor = trend === "up" ? "text-success" : trend === "down" ? "text-danger" : "text-ink-faint";

  return (
    <div className="relative p-4 bg-surface border border-border rounded-xl hover:border-accent/30 transition-all group">
      {/* Indicateur de tendance en haut à droite */}
      {trend && trendValue && (
        <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-medium">
          <span className={trendColor}>{trendIcon}</span>
          <span className="text-ink-faint">{trendValue}</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        {/* Cercle de progression optionnel */}
        {progress !== undefined ? (
          <div className="relative shrink-0">
            <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                className="stroke-surface-alt"
                strokeWidth="3"
              />
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                className={COLOR_STYLES[color] || "text-accent"}
                strokeWidth="3"
                strokeDasharray={`${progress * 1.003} 100`}
                strokeLinecap="round"
                style={{
                  transition: "stroke-dasharray 0.5s ease-out",
                  opacity: isLoaded ? 1 : 0,
                }}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-ink">
              {progress}%
            </span>
          </div>
        ) : null}

        {/* Valeur principale */}
        <div className={`flex-1 ${progress !== undefined ? "" : "text-center"}`}>
          <div
            className={`text-2xl font-black ${COLOR_STYLES[color] || "text-accent"} transition-transform duration-300 ${
              isLoaded ? "scale-100" : "scale-95"
            }`}
          >
            {value}
          </div>
          <div className="text-[10px] text-ink-muted mt-0.5 uppercase tracking-wide">
            {label}
          </div>
        </div>
      </div>

      {/* Barre de progression subtile en bas */}
      {progress !== undefined && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-surface-alt rounded-b-xl overflow-hidden">
          <div
            className={`h-full ${BG_COLOR_STYLES[color] || "bg-accent/10"} transition-all duration-500`}
            style={{ width: `${progress}%`, opacity: isLoaded ? 1 : 0 }}
          />
        </div>
      )}
    </div>
  );
}
